/* Kandes product tabs — behavior (revision 2, 11 Jul 2026).
   Tabs + deep links, spec leader-row formatting, lazy video hydration
   (YouTube/Instagram/MP4/Shopify), planner CTA card (opens the planner page -
   the storefront's X-Frame-Options: DENY forbids embedding), project lightbox, 3D-models gate (shares the
   CAD grant localStorage key with assets/kandes-lead.js so a customer is only
   ever asked once), quote-drawer title override, GA4 micro-events, and theme
   editor lifecycle events (shopify:section:load / shopify:block:select) so the
   section keeps working after editor re-renders.
   Self-contained vanilla JS; loaded only where the section renders. */
(function () {
  'use strict';

  /* Load guard (rev 2, sample-page fix): several components include this file
     (sample panel snippet + sample details section + trade snippet), and the
     browser executes every <script> element even with an identical src. The
     document-level delegated listeners below would then register N times —
     the trade toggle flips hidden twice per click and looks dead. Same
     pattern as assets/kandes-lead.js (__kandesLeadLoaded). */
  if (window.__kandesTabsLoaded) return;
  window.__kandesTabsLoaded = true;

  var CAD_STORAGE_KEY = 'kandesCadAccess'; // MUST match assets/kandes-lead.js
  var CAD_EXPIRY_DAYS = 365;

  /* ---------- GA4 (same triple mechanism as kandes-lead.js) ---------- */
  function track(name, params) {
    params = params || {};
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish(name, params);
      }
    } catch (e) { /* analytics must never break UX */ }
    try {
      window.dataLayer = window.dataLayer || [];
      var entry = { event: name };
      Object.keys(params).forEach(function (k) { entry[k] = params[k]; });
      window.dataLayer.push(entry);
      if (typeof window.gtag === 'function') { window.gtag('event', name, params); }
    } catch (e) { /* ditto */ }
  }

  /* ---------- Specifications: metafield HTML -> dotted-leader rows ----------
     The Liquid renders the raw rich-text metafield (SEO/no-JS fallback):
       <p><strong>Size:</strong> 1000x200 mm<br><strong>Thickness:</strong> ...</p>
     This splits each <p> at <br> boundaries and rebuilds label/value rows. */
  function formatSpecContainer(container) {
    var rows = document.createElement('div');
    var paragraphs = container.querySelectorAll('p');
    var ok = false;

    paragraphs.forEach(function (p) {
      var lines = [[]];
      p.childNodes.forEach(function (n) {
        if (n.nodeName === 'BR') { lines.push([]); return; }
        lines[lines.length - 1].push(n);
      });
      lines.forEach(function (nodes) {
        var text = nodes.map(function (n) { return n.textContent; }).join('').trim();
        if (!text) return;
        var strong = null;
        for (var i = 0; i < nodes.length; i++) {
          if (nodes[i].nodeName === 'STRONG' || nodes[i].nodeName === 'B') { strong = nodes[i]; break; }
        }
        if (strong) {
          var label = strong.textContent.replace(/:\s*$/, '').trim();
          var value = nodes.filter(function (n) { return n !== strong; })
            .map(function (n) { return n.textContent; }).join('').replace(/^\s*:?\s*/, '').trim();
          if (label && value) {
            var row = document.createElement('div');
            row.className = 'kpt-spec-row';
            row.innerHTML = '<span class="kpt-spec-row__k"></span><span class="kpt-spec-row__dots"></span><span class="kpt-spec-row__v"></span>';
            row.firstChild.textContent = label;
            row.lastChild.textContent = value;
            rows.appendChild(row);
            ok = true;
            return;
          }
        }
        var line = document.createElement('div');
        line.className = 'kpt-spec-line';
        line.textContent = text;
        rows.appendChild(line);
        ok = true;
      });
    });

    if (ok) container.replaceWith(rows);
    return ok;
  }

  /* A group can hold SEVERAL metafield renders (e.g. Product details = size
     metafield + material/colour metafield) — format every one, not just the
     first (10 Jul feedback: material & colour looked different from the rest). */
  function formatSpecGroup(group) {
    if (group.getAttribute('data-kpt-formatted')) return;
    var containers = Array.prototype.slice.call(group.querySelectorAll('.metafield-rich_text_field'));
    if (!containers.length) return;
    var any = false;
    containers.forEach(function (c) { if (formatSpecContainer(c)) any = true; });
    if (any) group.setAttribute('data-kpt-formatted', '1');
  }

  function formatSpecs(scope) {
    scope.querySelectorAll('.kpt-specs__group').forEach(formatSpecGroup);
  }

  /* ---------- Tabs ---------- */
  function initTabs(root) {
    if (root.getAttribute('data-kpt-init')) return;
    root.setAttribute('data-kpt-init', '1');
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[role="tab"]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[role="tabpanel"]'));
    if (!tabs.length) return;

    function activate(tab, opts) {
      opts = opts || {};
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      panels.forEach(function (p) {
        p.hidden = p.id !== tab.getAttribute('aria-controls');
      });
      var panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) hydratePanel(panel);
      if (!opts.silent) {
        track('product_tab_view', { tab_name: tab.getAttribute('data-kpt-anchor') || tab.textContent.trim() });
        if (opts.updateHash && tab.getAttribute('data-kpt-anchor')) {
          history.replaceState(null, '', '#' + tab.getAttribute('data-kpt-anchor'));
        }
      }
    }
    root.kptActivate = activate;
    root.kptTabs = tabs;

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () { activate(tab, { updateHash: true }); });
      tab.addEventListener('keydown', function (e) {
        var i = tabs.indexOf(tab);
        var next = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1 : -1;
        if (next < 0 && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        var t = tabs[(next + tabs.length) % tabs.length];
        t.focus();
        activate(t, { updateHash: true });
      });
    });

    function fromHash(silent) {
      var h = (location.hash || '').replace('#', '');
      if (!h) return false;
      var tab = tabs.filter(function (t) { return t.getAttribute('data-kpt-anchor') === h; })[0];
      if (tab) {
        activate(tab, { silent: !!silent });
        return true;
      }
      return false;
    }

    if (!fromHash(true)) activate(tabs[0], { silent: true });
    window.addEventListener('hashchange', function () {
      if (fromHash(false)) {
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('.kpt').forEach(initTabs);
    (scope || document).querySelectorAll('.kpt').forEach(initRail);
    formatSpecs(scope || document);
    if (cadAccessValid()) revealModels(scope || document);
  }

  /* ---------- Floating rail controller ----------
     The rail is position:fixed. This keeps it aligned with the content
     container's right edge, switches LARGE (near the top of the product page,
     paired with the gallery) ↔ compact (once the tabs are reached), and fades
     it out past the end of the consultation section. */
  function initRail(root) {
    var rail = root.querySelector('.kpt__rail');
    if (!rail || rail.getAttribute('data-kpt-rail-init')) return;
    rail.setAttribute('data-kpt-rail-init', '1');

    var layout = root.querySelector('.kpt__layout');
    var ticking = false;
    /* 10 Jul feedback: the static product panel (main-product section) owns the
       top of the page — the floating rail appears only below the fold, once
       that panel has scrolled away. Without a panel: legacy behavior. */
    var staticPanel = document.querySelector('[data-kandes-product-panel]');

    function update() {
      ticking = false;
      if (!window.matchMedia('(min-width: 1150px)').matches) return;
      var rect = layout.getBoundingClientRect();
      rail.style.right = Math.max(16, window.innerWidth - rect.right) + 'px';
      var hidden = false;
      if (staticPanel) {
        hidden = staticPanel.getBoundingClientRect().bottom > 120;
        rail.classList.remove('kpt__rail--large');
      } else {
        /* LARGE while the tabs section is still well below the viewport top */
        rail.classList.toggle('kpt__rail--large', rect.top > 320);
      }
      /* fade out once the end of section 2 (consult card) passes the rail */
      var limitEl = document.querySelector('.kcc') || root;
      var limitBottom = limitEl.getBoundingClientRect().bottom;
      var railBottom = rail.getBoundingClientRect().top + rail.offsetHeight;
      if (limitBottom < railBottom) hidden = true;
      rail.classList.toggle('kpt__rail--hidden', hidden);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
  }

  /* ---------- Sample → basket (10 Jul feedback round 4) ----------
     "Order a sample" adds the sample to the cart in place (so more samples can
     be added) and reveals the note under the button. The href stays a cart
     permalink as no-JS fallback; on AJAX failure we follow it. */
  document.addEventListener('click', function (e) {
    var link = e.target.closest('a[data-kandes-add-sample]');
    if (!link) return;
    e.preventDefault();
    if (link.getAttribute('data-busy')) return;
    link.setAttribute('data-busy', '1');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(link.getAttribute('data-kandes-add-sample')), quantity: 1 }] })
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return r.json();
    }).then(function () {
      var note = link.parentElement.querySelector('[data-kandes-sample-note]');
      if (note) note.hidden = false;
      track('sample_added_to_cart', { product: link.getAttribute('data-product-title') || '' });
      /* nudge the theme cart UI (drawer/bubble) to refetch, if it listens */
      try {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true }));
      } catch (err) { /* cosmetic only */ }
    }).catch(function () {
      window.location.href = link.href; /* checkout permalink fallback */
    }).finally(function () {
      link.removeAttribute('data-busy');
    });
  });

  /* ---------- Trade block toggle (panel + rail): faint line under the sample
     button expands the VAT verification mini-form ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-kandes-trade-toggle]');
    if (!t) return;
    var box = document.getElementById(t.getAttribute('aria-controls'));
    if (!box) return;
    box.hidden = !box.hidden;
    t.setAttribute('aria-expanded', box.hidden ? 'false' : 'true');
  });

  /* ---------- Trade-sample verification (VIES → shared code) ----------
     Same contract as sections/kandes-trade-sample.liquid:
     POST {gateway}/trade-sample { email, vat, source_url } →
     { ok, valid: true|false|null, code, company }. */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-kpt-trade]');
    if (!form) return;
    e.preventDefault();
    if (form.getAttribute('data-busy')) return;
    var result = form.parentElement.querySelector('[data-kpt-trade-result]');
    var vat = form.elements.vat.value.trim();
    var email = form.elements.email.value.trim();
    var trap = form.elements.kts_x1.value.trim();
    var MSG = {
      fill: 'Please add both your VAT number and email.',
      success: 'Verified — welcome!',
      note: 'Enter this code at checkout and your samples come with our compliments.',
      recheck: 'Please double-check the number — EU VAT IDs start with the country prefix, e.g. DE123456789. You can also write to info@kandes.ch and we will set you up.',
      pending: 'Thank you — we have your details and will confirm your trade access within one business day.',
      retry: 'Please try again in a few minutes, or write to info@kandes.ch.'
    };
    if (trap) { result.textContent = MSG.pending; return; }
    if (!vat || !email) { result.textContent = MSG.fill; return; }

    var rootEl = form.closest('[data-kandes-gateway]') || document.querySelector('[data-kandes-gateway]');
    var gateway = rootEl ? (rootEl.getAttribute('data-kandes-gateway') || '').replace(/\/+$/, '') : '';
    if (!gateway) { result.textContent = MSG.retry; return; }

    form.setAttribute('data-busy', '1');
    var btn = form.querySelector('button');
    var btnLabel = btn.querySelector('.btn-text');
    var idle = btnLabel ? btnLabel.textContent : '';
    btn.disabled = true;
    if (btnLabel) btnLabel.textContent = '…';

    fetch(gateway + '/trade-sample', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, vat: vat, source_url: location.href })
    }).then(function (r) { return r.json().then(function (b) { return { status: r.status, body: b }; }); })
      .then(function (res) {
        var b = res.body || {};
        if (b.ok && b.valid === true && b.code) {
          var who = b.company ? ' (' + b.company + ')' : '';
          result.innerHTML = '';
          var p1 = document.createElement('p'); p1.style.margin = '0'; p1.textContent = MSG.success + who;
          var code = document.createElement('span'); code.className = 'kpt-trade__code'; code.textContent = b.code;
          var p2 = document.createElement('p'); p2.style.margin = '6px 0 0'; p2.textContent = MSG.note;
          result.appendChild(p1); result.appendChild(code); result.appendChild(p2);
          track('trade_sample_verify', { valid: true, placement: 'product_tabs' });
        } else if (b.ok && b.valid === false) {
          result.textContent = MSG.recheck;
          track('trade_sample_verify', { valid: false, placement: 'product_tabs' });
        } else if (b.ok) {
          result.textContent = MSG.pending;
        } else {
          result.textContent = MSG.retry;
        }
      })
      .catch(function () { result.textContent = MSG.retry; })
      .finally(function () {
        form.removeAttribute('data-busy');
        btn.disabled = false;
        if (btnLabel) btnLabel.textContent = idle;
      });
  });

  /* ---------- Video hydration ---------- */
  function youtubeId(url) {
    var m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
    return m ? m[1] : null;
  }
  function instagramCode(url) {
    var m = url.match(/instagram\.com\/(?:p|reel|tv)\/([\w-]+)/);
    return m ? m[1] : null;
  }

  function buildEmbed(el, forceAutoplay) {
    var url = el.getAttribute('data-video-url') || '';
    var autoplay = forceAutoplay || el.getAttribute('data-autoplay') === 'true';
    var controls = el.getAttribute('data-controls') !== 'false';
    var loop = el.getAttribute('data-loop') === 'true';
    var yt = youtubeId(url);
    var ig = instagramCode(url);
    var node;
    if (yt) {
      node = document.createElement('iframe');
      node.src = 'https://www.youtube-nocookie.com/embed/' + yt +
        '?rel=0&playsinline=1' +
        (autoplay ? '&autoplay=1&mute=1' : '') +
        (controls ? '' : '&controls=0') +
        (loop ? '&loop=1&playlist=' + yt : '');
      node.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      node.setAttribute('allowfullscreen', '');
      node.title = el.getAttribute('data-title') || 'Video';
    } else if (ig) {
      node = document.createElement('iframe');
      node.src = 'https://www.instagram.com/p/' + ig + '/embed/';
      node.allow = 'encrypted-media';
      node.title = el.getAttribute('data-title') || 'Instagram video';
    } else if (url) {
      node = document.createElement('video');
      node.src = url;
      node.playsInline = true;
      node.controls = controls;
      node.loop = loop;
      if (autoplay) { node.muted = true; node.autoplay = true; }
      var poster = el.getAttribute('data-poster');
      if (poster) node.poster = poster;
    }
    if (!node) return;
    el.innerHTML = '';
    el.appendChild(node);
    el.setAttribute('data-hydrated', 'true');
    if (node.tagName === 'VIDEO' && autoplay) {
      var p = node.play();
      if (p && p.catch) p.catch(function () { /* autoplay blocked — controls remain */ });
    }
  }

  document.addEventListener('click', function (e) {
    var facade = e.target.closest('[data-kpt-facade]');
    if (!facade) return;
    var frame = facade.closest('[data-kpt-video]');
    if (!frame || frame.getAttribute('data-hydrated')) return;
    buildEmbed(frame, true);
    track('video_play', { video_title: frame.getAttribute('data-title') || '' });
  });

  /* Native Shopify-hosted <video> (rendered by Liquid, preload=none): just track. */
  document.addEventListener('play', function (e) {
    var v = e.target;
    if (v.tagName === 'VIDEO' && v.closest('[data-kpt-shopify-video]') && !v.getAttribute('data-kpt-tracked')) {
      v.setAttribute('data-kpt-tracked', '1');
      track('video_play', { video_title: v.closest('[data-kpt-shopify-video]').getAttribute('data-title') || '' });
    }
  }, true);

  function hydratePanel(panel) {
    /* Autoplay embeds hydrate when their tab first becomes visible. */
    panel.querySelectorAll('[data-kpt-video][data-autoplay="true"]:not([data-hydrated])').forEach(function (el) {
      buildEmbed(el);
    });
    /* Autoplaying Shopify-hosted videos (muted) start when visible. */
    panel.querySelectorAll('[data-kpt-shopify-video][data-autoplay="true"] video').forEach(function (v) {
      v.muted = true;
      var p = v.play();
      if (p && p.catch) p.catch(function () { /* blocked — fine */ });
    });
  }

  /* ---------- Quote-drawer title override (e.g. "Get an installation quote") ---------- */
  var drawerTitleDefault = null;
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-kandes-open="quote"]');
    if (!opener) return;
    var titleEl = document.querySelector('#KandesQuoteDrawer .kandes-drawer__title');
    if (!titleEl) return;
    if (drawerTitleDefault === null) drawerTitleDefault = titleEl.textContent;
    var custom = opener.getAttribute('data-kpt-drawer-title');
    titleEl.textContent = custom || drawerTitleDefault;
  });

  /* ---------- Project lightbox ---------- */
  var lightbox = null;
  var lbItems = [];
  var lbIndex = 0;

  function ensureLightbox() {
    if (lightbox) return lightbox;
    lightbox = document.createElement('div');
    lightbox.className = 'kpt-lightbox';
    lightbox.hidden = true;
    /* 10 Jul feedback round 4: smaller stage, X and arrows INSIDE the image
       area (the old X sat above the stage and could end up off-screen —
       nothing visible to close with). Backdrop click + Esc still close. */
    lightbox.innerHTML =
      '<div class="kpt-lightbox__backdrop" data-kpt-lb-close></div>' +
      '<div class="kpt-lightbox__stage">' +
        '<div class="kpt-lightbox__media" data-kpt-lb-media></div>' +
        '<button type="button" class="kpt-lightbox__btn kpt-lightbox__close" data-kpt-lb-close aria-label="Close">&times;</button>' +
        '<button type="button" class="kpt-lightbox__btn kpt-lightbox__nav kpt-lightbox__nav--prev" data-kpt-lb-prev aria-label="Previous">&#8592;</button>' +
        '<button type="button" class="kpt-lightbox__btn kpt-lightbox__nav kpt-lightbox__nav--next" data-kpt-lb-next aria-label="Next">&#8594;</button>' +
        '<div class="kpt-lightbox__bar">' +
          '<span data-kpt-lb-caption></span>' +
        '</div>' +
      '</div>';
    document.body.appendChild(lightbox);
    lightbox.addEventListener('click', function (e) {
      if (e.target.closest('[data-kpt-lb-close]')) closeLightbox();
      if (e.target.closest('[data-kpt-lb-prev]')) showItem(lbIndex - 1);
      if (e.target.closest('[data-kpt-lb-next]')) showItem(lbIndex + 1);
    });
    document.addEventListener('keydown', function (e) {
      if (lightbox.hidden) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') showItem(lbIndex - 1);
      if (e.key === 'ArrowRight') showItem(lbIndex + 1);
    });
    return lightbox;
  }

  function showItem(i) {
    if (!lbItems.length) return;
    lbIndex = (i + lbItems.length) % lbItems.length;
    var item = lbItems[lbIndex];
    var media = lightbox.querySelector('[data-kpt-lb-media]');
    var caption = lightbox.querySelector('[data-kpt-lb-caption]');
    media.innerHTML = '';
    if (item.type === 'image') {
      var img = document.createElement('img');
      img.src = item.src;
      img.alt = item.caption || '';
      media.appendChild(img);
    } else {
      var holder = document.createElement('div');
      holder.setAttribute('data-kpt-video', '');
      holder.setAttribute('data-video-url', item.src);
      holder.setAttribute('data-controls', 'true');
      holder.style.position = 'absolute';
      holder.style.inset = '0';
      media.appendChild(holder);
      buildEmbed(holder, true);
    }
    caption.textContent = (item.caption ? item.caption + ' — ' : '') + (lbIndex + 1) + ' / ' + lbItems.length;
    lightbox.querySelectorAll('.kpt-lightbox__nav').forEach(function (b) {
      b.hidden = lbItems.length < 2;
    });
  }

  function closeLightbox() {
    lightbox.hidden = true;
    lightbox.querySelector('[data-kpt-lb-media]').innerHTML = '';
    document.documentElement.style.overflow = '';
  }

  document.addEventListener('click', function (e) {
    var proj = e.target.closest('[data-kpt-project]');
    if (!proj) return;
    var script = proj.querySelector('script[type="application/json"]');
    if (!script) return;
    try { lbItems = JSON.parse(script.textContent) || []; } catch (err) { lbItems = []; }
    if (!lbItems.length) return;
    ensureLightbox().hidden = false;
    document.documentElement.style.overflow = 'hidden';
    showItem(0);
    track('project_gallery_open', { project_name: proj.getAttribute('data-project-name') || '' });
  });

  /* ---------- 3D models gate (shared grant with the CAD page) ---------- */
  function cadAccessValid() {
    try {
      var v = window.localStorage.getItem(CAD_STORAGE_KEY);
      if (!v || v.indexOf('granted:') !== 0) return false;
      var granted = new Date(v.slice(8));
      return !isNaN(granted) && (Date.now() - granted.getTime()) < CAD_EXPIRY_DAYS * 864e5;
    } catch (e) { return false; }
  }

  function revealModels(scope) {
    scope.querySelectorAll('[data-kpt-models-gate]').forEach(function (g) { g.hidden = true; });
    scope.querySelectorAll('[data-kpt-models-content]').forEach(function (c) { c.hidden = false; });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-kpt-form="cad"]');
    if (!form) return;
    e.preventDefault();
    if (!form.checkValidity()) { form.reportValidity(); return; }

    var rootEl = form.closest('[data-kandes-gateway]') || document.querySelector('[data-kandes-gateway]');
    var gateway = rootEl ? (rootEl.getAttribute('data-kandes-gateway') || '').replace(/\/+$/, '') : '';
    var errorEl = form.querySelector('[data-kandes-error]');
    var btn = form.querySelector('button[type="submit"]');
    if (errorEl) errorEl.hidden = true;
    if (btn) btn.disabled = true;

    var payload = {
      locale: (document.documentElement.lang || 'en').split('-')[0],
      source_url: window.location.href.split('#')[0]
    };
    new FormData(form).forEach(function (value, key) {
      if (key === 'consent') return;
      payload[key] = String(value).trim();
    });
    payload.consent = !!form.querySelector('input[name="consent"]').checked;

    // Honeypot: DOM field is kandes_x1 (Chrome autofill killed name="website");
    // the gateway contract key stays "website".
    if ('kandes_x1' in payload) { payload.website = payload.kandes_x1; delete payload.kandes_x1; }

    function fail() {
      if (btn) btn.disabled = false;
      if (errorEl) errorEl.hidden = false;
    }

    if (!gateway) { fail(); return; }
    fetch(gateway + '/lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) { return r.json(); }).then(function (body) {
      if (body && body.ok) {
        try { window.localStorage.setItem(CAD_STORAGE_KEY, 'granted:' + new Date().toISOString()); }
        catch (err) { /* private mode — still reveal this visit */ }
        track('cad_access_request', { placement: 'product_tabs' });
        revealModels(document);
      } else { fail(); }
    }).catch(fail);
  });

  /* ---------- Secondary intent clicks (retargeting signals) ---------- */
  document.addEventListener('click', function (e) {
    var el = e.target.closest('[data-kpt-track]');
    if (!el) return;
    track(el.getAttribute('data-kpt-track'), {
      product: el.getAttribute('data-product-title') || '',
      placement: 'product_tabs'
    });
  });

  /* ---------- Boot + theme editor lifecycle ----------
     The editor re-renders sections on every setting change; without these
     listeners tabs stop switching after editor re-renders. */
  document.addEventListener('DOMContentLoaded', function () { initAll(document); });

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  /* Selecting a block in the editor sidebar activates the tab that contains it. */
  document.addEventListener('shopify:block:select', function (e) {
    var panel = e.target && e.target.closest ? e.target.closest('.kpt__panel') : null;
    if (!panel) return;
    var root = panel.closest('.kpt');
    if (!root || !root.kptTabs) return;
    var tab = root.kptTabs.filter(function (t) {
      return t.getAttribute('aria-controls') === panel.id;
    })[0];
    if (tab) root.kptActivate(tab, { silent: true });
  });
})();